import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  type DocumentSendSortField,
  documentSendApi,
  type DocumentSendFilters,
  type DocumentSendListItem,
} from '@/entities/DocumentSend';
import {
  type Pagination,
  type SortOrder,
  useCustomToast,
  useInfiniteScrollObserver,
  useUpdateUrl,
} from '@/shared/lib';

import {
  areDocumentSendFiltersEqual,
  buildDocumentSendControlFilterQuery,
  hasActiveDocumentSendFilters,
  normalizeDocumentSendSortOptions,
  readDocumentSendControlQuery,
} from './document-send-control-query.util';

const DEFAULT_ITEMS_PER_PAGE = 10;
const SHOW_ALL_ITEMS_PER_PAGE = 100;
const SHOW_ALL_BATCH_SIZE = 30;

type ItemsPerPageOption = number;

function getHasMoreItems(metadata: Pagination | undefined, loadedItemsCount: number): boolean {
  if (!metadata) {
    return false;
  }

  if (typeof metadata.isNextPage === 'boolean') {
    return metadata.isNextPage;
  }

  if (typeof metadata.page === 'number' && typeof metadata.totalPages === 'number') {
    return metadata.page < metadata.totalPages;
  }

  return loadedItemsCount < (metadata.total ?? loadedItemsCount);
}

export function useDocumentSendControlList() {
  const route = useRoute();
  const { updateUrl } = useUpdateUrl();
  const toast = useCustomToast();
  const initialQueryState = readDocumentSendControlQuery(route.query as Record<string, unknown>);

  const documentSends = ref<DocumentSendListItem[]>([]);
  const allDocumentSends = ref<DocumentSendListItem[]>([]);
  const metadata = ref<Pagination | null>(null);
  const currentPage = ref(initialQueryState.currentPage);
  const totalItems = ref(0);
  const selectedItemsPerPage = ref<ItemsPerPageOption>(DEFAULT_ITEMS_PER_PAGE);
  const loading = ref(false);
  const loadingMore = ref(false);
  const hasMoreItems = ref(false);
  const hasLoaded = ref(false);
  const searchTerm = ref(initialQueryState.searchTerm);
  const sort = ref<DocumentSendSortField | ''>(initialQueryState.sort);
  const order = ref<SortOrder | ''>(initialQueryState.order);
  const filters = ref<DocumentSendFilters>(initialQueryState.filters);

  const isInfiniteScrollMode = computed(
    () => selectedItemsPerPage.value === SHOW_ALL_ITEMS_PER_PAGE
  );

  const itemsPerPageOptions = computed(() => [
    { id: DEFAULT_ITEMS_PER_PAGE, name: '10 por página' },
    { id: 25, name: '25 por página' },
    { id: 50, name: '50 por página' },
    { id: SHOW_ALL_ITEMS_PER_PAGE, name: 'Todos os envios' },
  ]);

  const normalizedSearchTerm = computed(() => searchTerm.value.trim());
  const isFilterActive = computed(() => hasActiveDocumentSendFilters(filters.value));
  const hasDocumentSends = computed(
    () => (isInfiniteScrollMode.value ? allDocumentSends.value : documentSends.value).length > 0
  );
  const resolvedItemsPerPage = computed(() => {
    if (isInfiniteScrollMode.value) {
      return SHOW_ALL_BATCH_SIZE;
    }

    return selectedItemsPerPage.value;
  });
  const shouldShowNoResults = computed(
    () =>
      hasLoaded.value &&
      !loading.value &&
      totalItems.value === 0 &&
      (normalizedSearchTerm.value.length > 0 || isFilterActive.value)
  );
  const shouldShowEmptyState = computed(
    () =>
      hasLoaded.value &&
      !loading.value &&
      totalItems.value === 0 &&
      normalizedSearchTerm.value.length === 0 &&
      !isFilterActive.value
  );

  async function syncUrl(page: number): Promise<void> {
    await updateUrl({
      search: searchTerm.value,
      page,
      sort:
        sort.value && order.value
          ? {
              sort: sort.value,
              order: order.value,
            }
          : undefined,
      filter: buildDocumentSendControlFilterQuery(filters.value),
    });
  }

  async function loadDocumentSends(page = currentPage.value, append = false): Promise<void> {
    if (append) {
      loadingMore.value = true;
    } else {
      loading.value = true;
    }

    try {
      const response = await documentSendApi.getDocumentSends({
        page,
        itemsPerPage: resolvedItemsPerPage.value,
        term: normalizedSearchTerm.value.length > 0 ? normalizedSearchTerm.value : undefined,
        ...filters.value,
        ...(sort.value && order.value
          ? {
              sort: sort.value,
              order: order.value,
            }
          : {}),
      });

      const nextItems = response.data ?? [];
      const nextMetadata = response.metadata;

      if (isInfiniteScrollMode.value) {
        allDocumentSends.value = append
          ? [...allDocumentSends.value, ...nextItems]
          : [...nextItems];
        hasMoreItems.value = getHasMoreItems(nextMetadata, allDocumentSends.value.length);
      } else {
        allDocumentSends.value = [];
        hasMoreItems.value = false;
      }

      documentSends.value = nextItems;
      metadata.value = nextMetadata ?? null;
      currentPage.value = nextMetadata?.page ?? page;
      totalItems.value = nextMetadata?.total ?? 0;
    } catch {
      if (!append) {
        documentSends.value = [];
        allDocumentSends.value = [];
        metadata.value = null;
        currentPage.value = 1;
        totalItems.value = 0;
        hasMoreItems.value = false;
      }

      toast.showErrorToast({
        title: 'Erro ao carregar envios',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    } finally {
      loading.value = false;
      loadingMore.value = false;
      hasLoaded.value = true;
    }
  }

  async function handlePageChanged(page: number): Promise<void> {
    if (isInfiniteScrollMode.value) {
      return;
    }

    currentPage.value = page;
    await syncUrl(page);
    await loadDocumentSends(page);
  }

  async function loadMoreDocumentSends(): Promise<void> {
    if (!isInfiniteScrollMode.value || loadingMore.value || !hasMoreItems.value) {
      return;
    }

    await loadDocumentSends(currentPage.value + 1, true);
  }

  useInfiniteScrollObserver({
    enabled: isInfiniteScrollMode,
    loadingMore,
    hasMore: hasMoreItems,
    onLoadMore: loadMoreDocumentSends,
  });

  async function handleItemsPerPageChange(value: string | number): Promise<void> {
    const nextValue = Number(value);

    const allowedValues = itemsPerPageOptions.value.map((o) => o.id);
    if (!allowedValues.includes(nextValue)) {
      return;
    }

    selectedItemsPerPage.value = nextValue;
    currentPage.value = 1;

    if (nextValue !== SHOW_ALL_ITEMS_PER_PAGE) {
      hasMoreItems.value = false;
    }

    await syncUrl(1);
    await loadDocumentSends(1);
  }

  async function handleSearch(term: string): Promise<void> {
    searchTerm.value = term;
    currentPage.value = 1;
    await syncUrl(1);
    await loadDocumentSends(1);
  }

  async function handleFiltersApplied(nextFilters: DocumentSendFilters): Promise<void> {
    filters.value = { ...nextFilters };
    currentPage.value = 1;
    await syncUrl(1);
    await loadDocumentSends(1);
  }

  async function handleSortOptions(nextSort: string, nextOrder: string): Promise<void> {
    const { sort: normalizedSort, order: normalizedOrder } = normalizeDocumentSendSortOptions(
      nextSort,
      nextOrder
    );

    if (
      (normalizedSort === '' && normalizedOrder !== '') ||
      (normalizedSort !== '' && normalizedOrder === '')
    ) {
      return;
    }

    sort.value = normalizedSort;
    order.value = normalizedOrder;
    currentPage.value = 1;

    await syncUrl(1);
    await loadDocumentSends(1);
  }

  async function handleDeleteDocumentSend(id: number): Promise<void> {
    try {
      await documentSendApi.deleteDocumentSend(id);

      if (isInfiniteScrollMode.value) {
        allDocumentSends.value = [];
        currentPage.value = 1;
        hasMoreItems.value = false;
        await loadDocumentSends(1);
      } else {
        const isLastOnPage = documentSends.value.length === 1 && currentPage.value > 1;
        const nextPage = isLastOnPage ? currentPage.value - 1 : currentPage.value;

        if (isLastOnPage) {
          await syncUrl(nextPage);
        }

        await loadDocumentSends(nextPage);
      }

      toast.showSuccessToast({
        title: 'Envio excluído com sucesso',
        type: 'success',
      });
    } catch {
      toast.showErrorToast({
        title: 'Erro ao excluir envio. Tente novamente',
        type: 'error',
      });
    }
  }

  watch(
    () => route.query,
    async (query) => {
      const nextQueryState = readDocumentSendControlQuery(query as Record<string, unknown>);

      if (
        nextQueryState.searchTerm === searchTerm.value &&
        nextQueryState.currentPage === currentPage.value &&
        nextQueryState.sort === sort.value &&
        nextQueryState.order === order.value &&
        areDocumentSendFiltersEqual(nextQueryState.filters, filters.value)
      ) {
        return;
      }

      searchTerm.value = nextQueryState.searchTerm;
      currentPage.value = nextQueryState.currentPage;
      sort.value = nextQueryState.sort;
      order.value = nextQueryState.order;
      filters.value = nextQueryState.filters;

      await loadDocumentSends(nextQueryState.currentPage);
    },
    { deep: true }
  );

  return {
    allDocumentSends,
    currentPage,
    documentSends,
    filters,
    handleDeleteDocumentSend,
    handleFiltersApplied,
    handleSortOptions,
    hasDocumentSends,
    hasLoaded,
    hasMoreItems,
    handleItemsPerPageChange,
    handlePageChanged,
    handleSearch,
    isFilterActive,
    isInfiniteScrollMode,
    itemsPerPageOptions,
    loadDocumentSends,
    loadMoreDocumentSends,
    loading,
    loadingMore,
    metadata,
    order,
    resolvedItemsPerPage,
    searchTerm,
    selectedItemsPerPage,
    sort,
    shouldShowEmptyState,
    shouldShowNoResults,
    totalItems,
  };
}
